import { Key } from 'ts-key-enum';

/**
 * Keyboard-shortcut combo helpers.
 *
 * A workflow shortcut is a single opaque string in react-hotkeys-hook combo
 * syntax, normalized to the backend's `"mod+shift+d"` convention: lowercase,
 * `+`-separated, the portable primary modifier `mod` (Cmd on macOS / Ctrl on
 * Windows) first, then optional `shift`/`alt`, then the terminal key. These
 * helpers keep the *stored* combo, the *recorded* combo (from
 * `useRecordHotkeys`) and the *fired* combo (from a `useHotkeys` press) in this
 * one canonical shape so they round-trip and compare equal.
 */

/** Backend `max_length` for the `shortcut` field. */
export const SHORTCUT_MAX_LENGTH = 64;

/** Canonical modifier ordering shared by stored combos and press reconstruction. */
const MODIFIER_ORDER = ['mod', 'shift', 'alt'] as const;
type ModifierToken = (typeof MODIFIER_ORDER)[number];

/**
 * Fold platform/library modifier aliases onto the canonical token. Both the
 * macOS Command key and the Windows/Linux Control key collapse to the portable
 * `mod`, matching react-hotkeys-hook's `mod` and the backend contract.
 */
const MODIFIER_ALIASES: Record<string, ModifierToken> = {
  meta: 'mod',
  ctrl: 'mod',
  control: 'mod',
  command: 'mod',
  cmd: 'mod',
  option: 'alt',
};

const isModifier = (token: string): token is ModifierToken =>
  (MODIFIER_ORDER as readonly string[]).includes(token);

export const normalizeToken = (raw: string): string => {
  const lower = raw.trim().toLowerCase();
  return MODIFIER_ALIASES[lower] ?? lower;
};

/** Order tokens as `[canonical modifiers, ...sorted terminal keys]`, de-duped. */
const orderTokens = (tokens: string[]): string[] => {
  const normalized = tokens.map(normalizeToken).filter((token) => token.length > 0);
  const unique = Array.from(new Set(normalized));
  const modifiers = unique
    .filter(isModifier)
    .sort((a, b) => MODIFIER_ORDER.indexOf(a) - MODIFIER_ORDER.indexOf(b));
  const keys = unique.filter((token) => !isModifier(token)).sort();
  return [...modifiers, ...keys];
};

/** Canonicalise a combo string so equivalent combos compare equal. */
export const normalizeCombo = (combo: string): string =>
  orderTokens(combo.split('+')).join('+');

/** Build a canonical combo from the `Set<string>` produced by `useRecordHotkeys`. */
export const normalizeRecordedKeys = (keys: Iterable<string>): string =>
  orderTokens(Array.from(keys)).join('+');

/** Split a (normalized) combo into its ordered tokens. */
export const comboTokens = (combo: string): string[] =>
  combo.split('+').filter((token) => token.length > 0);

/** A combo is only runnable once it holds a terminal (non-modifier) key. */
export const hasTerminalKey = (combo: string): boolean =>
  comboTokens(combo).some((token) => !isModifier(normalizeToken(token)));

/**
 * A combo must carry a modifier. The listener is mounted app-wide and fires on
 * content-editable fields, so a bare terminal key would run a routine while a
 * clinician is typing into a note.
 */
export const hasModifier = (combo: string): boolean =>
  comboTokens(combo).some((token) => isModifier(normalizeToken(token)));

/**
 * A combo is assignable when it has a modifier plus a terminal key and fits the
 * backend limit.
 */
export const isAssignableCombo = (combo: string): boolean =>
  hasTerminalKey(combo) && hasModifier(combo) && combo.length <= SHORTCUT_MAX_LENGTH;

/**
 * react-hotkeys-hook invokes handlers with a `HotkeysEvent` carrying the parsed
 * modifiers as booleans plus the terminal `keys`. Reconstruct the canonical
 * combo so the listener can map a press back to a workflow. `mod`, `meta` and
 * `ctrl` all fold to the portable `mod` token.
 */
export interface FiredHotkey {
  mod?: boolean;
  meta?: boolean;
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  keys?: readonly string[];
}

export const comboFromHotkeysEvent = (event: FiredHotkey): string => {
  const tokens: string[] = [];
  if (event.mod || event.meta || event.ctrl) tokens.push('mod');
  if (event.shift) tokens.push('shift');
  if (event.alt) tokens.push('alt');
  tokens.push(...(event.keys ?? []));
  return normalizeRecordedKeys(tokens);
};

export type ShortcutKbdToken =
  | { type: 'modifier'; key: Key }
  | { type: 'text'; label: string };

// `mod` renders as the platform primary modifier: `<Kbd>` shows ⌘ on macOS and
// "Ctrl" on Windows for `Key.Meta`, which matches `mod`'s portable meaning.
const MODIFIER_KEY_BY_TOKEN: Record<string, Key> = {
  mod: Key.Meta,
  shift: Key.Shift,
  alt: Key.Alt,
  arrowup: Key.ArrowUp,
  arrowdown: Key.ArrowDown,
  arrowleft: Key.ArrowLeft,
  arrowright: Key.ArrowRight,
};

/** Map a combo to `<Kbd>`-ready tokens (glyph modifiers + text keys). */
export const comboToKbdTokens = (combo: string): ShortcutKbdToken[] =>
  comboTokens(combo).map((raw) => {
    const token = normalizeToken(raw);
    const modifierKey = MODIFIER_KEY_BY_TOKEN[token];
    if (modifierKey !== undefined) {
      return { type: 'modifier', key: modifierKey };
    }
    return { type: 'text', label: token.length === 1 ? token.toUpperCase() : token };
  });
